// Design System v3 — Palette Orange #FF6A00 + Navy #0B1220
// Règle : tout token couleur passe par ce fichier. Aucun hex ou rgba inline.
//
// ═══ DEUX PALETTES, TROIS FAÇONS DE LES LIRE ════════════════════════════════
//
// `rawColors`  — valeurs LITTÉRALES du thème clair (#f0b90b…).
// `darkColors` — leurs équivalents sombres.
// `colors`     — ce que consomme l'application : des `var(--c-*)`.
//
// ## Pourquoi `colors` ne contient plus de hex
// 798 couleurs sont posées en style INLINE depuis JS
// (`style={{ color: colors.gray500 }}`), réparties dans 129 fichiers. Un style
// inline ne réagit à AUCUNE classe CSS : la variante `dark:` de Tailwind ne
// pouvait donc rien pour eux. En pointant vers une variable CSS, ces 798 usages
// suivent le thème sans qu'on touche à un seul de ces fichiers.
//
// ## ⚠️ Quand `colors` ne convient PAS
// Une `var(--c-x)` n'a de sens que dans du CSS. Partout où une couleur est
// remise à du NON-CSS — le SDK Google Maps, un canevas, une lib tierce —, il
// faut une valeur littérale : utiliser `themeColor()`, qui résout la variable
// à l'instant T. Passer `colors.*` à ces API ne dessine rien du tout.

export const rawColors = {
  // ── Orange — Énergie · Confiance · Dynamisme ──
  // ── Marque : OR ambré (2026-07-21) ────────────────────────────────────────
  // Remplace l'orange #ff6a00. La rampe garde les noms `or*` : ils sont
  // consommés par ~90 fichiers et signifient « couleur de marque », pas
  // « orange » — les renommer n'apporterait rien et casserait tout.
  //
  // ⚠️ RÈGLE DE CONTRASTE (audit Base) : l'or est CLAIR, donc
  //   • or500 = FOND uniquement, avec du texte NAVY dessus (7.9:1 ✓).
  //     Du blanc sur or500 ne ferait que 2.4:1 — illisible.
  //   • or700 = seul cran valide pour du TEXTE/lien sur fond clair (5.1:1 ✓).
  or900: "#4a3a02",
  or700: "#8a6a00", // texte & liens sur fond clair — AA 5.07:1
  or600: "#c99a06", // bordures, états pressés
  or500: "#f0b90b", // ★ brand primary — FOND de CTA (texte navy : 10.4:1)
  or300: "#f7d979",
  or100: "#fdf0c8",
  or50:  "#fffaeb",

  // ── Navy — Sérénité · Sécurité · Professionnalisme ──
  navy900: "#050912",
  navy800: "#0b1220", // ★ brand dark
  navy700: "#182338", // haut du dégradé de la carte solde (v4 premium)
  navy600: "#1c2842",
  navy400: "#4a5778",
  navy200: "#b6bccb",
  navy50:  "#f1f3f7",

  // ── SOS — Urgence ──
  sos600: "#d81325",
  sos400: "#ee2d3f",
  sos200: "#fde2e5",

  // ── Succès ──
  // Règle de contraste (Base) : le cran VIF (500) sert aux FONDS de badge et
  // aux remplissages ; le TEXTE vert sur fond clair passe par le cran 600,
  // calibré ≥ 4.5:1 (success500 sur blanc = 2.6:1, illisible en petit corps).
  success600: "#0e8345",
  success500: "#14b774",
  success100: "#ddf5e8",

  // ── Warning — feedback système (JAMAIS la marque) ──
  // Famille dédiée : un avertissement ne doit pas porter l'orange de MARQUE,
  // sinon « en attente de validation » et « moment de marque » sont identiques
  // (anti-pattern Base sur marque chaude). warn600 = texte (4.5:1 sur blanc),
  // warn50 = fond doux de badge/banner, warn300 = fond saturé (texte navy
  // uniquement). Les tokens or* ne servent JAMAIS au feedback système.
  warn600: "#9f6402",
  warn300: "#f6bc2f",
  warn50:  "#fdf2dc",

  // ── Gris neutres (froids) ──
  // Design 100 % blanc/neutre (préférence PO 2026-07-18) : la palette "Premium
  // Warm" (sable/stone) a été RETIRÉE — aucune teinte sable ne doit exister
  // dans le design system. Fonds d'écran = blanc ; gris uniquement pour bordures,
  // séparateurs, texte secondaire et remplissages de champs.
  gray500: "#6b7387",
  gray300: "#c7cbd6",
  gray200: "#dde0e8",
  gray100: "#eef0f5",
  gray50:  "#f7f8fb",

  // ── Émeraude — Live / Disponibilité / Progression ──
  // Distincte du vert `success` ET du rouge SOS, pour ne pas brouiller la
  // hiérarchie d'alerte. Deux usages :
  //   • états "en cours / Main disponible / en route" ;
  //   • accent de PROGRESSION dans les formulaires longs (étapes franchies,
  //     confirmations d'étape) — depuis le 2026-08-02, où l'or reste la couleur
  //     d'ACTION (CTA) et l'émeraude celle de la VALIDATION acquise.
  // ⚠️ emerald600 ne fait que 3.87:1 sur blanc → JAMAIS pour du texte.
  // emerald700 (5.36:1) pour les textes/liens, 600 et 500 pour les aplats.
  emerald700: "#0a7a52",
  emerald600: "#0c9463",
  emerald500: "#0ea871",
  emerald100: "#d9f2e6",
  emerald50:  "#eefaf4", // fonds d'encadré très doux

  // ── Or — Mérite / Premium ──
  // Notes (⭐) et tier "La Main d'Or". Plus jaune que `or300` : lit comme de l'or,
  // pas comme la marque orange.
  gold500: "#e8a13c",
  gold300: "#ffcf87",

  // ── Surfaces ──
  black:  "#0b1220",
  white:  "#ffffff",
  cream:  "#fafaf7",

  // ── Texte sur fond sombre (alpha whites) ──
  // À utiliser quand le texte est posé sur un gradient/surface dark.
  onDark:        "#ffffff",                 // texte primaire
  onDarkStrong:  "rgba(255,255,255,0.85)",  // sous-titres
  onDarkMuted:   "rgba(255,255,255,0.72)",  // eyebrows, hints
  onDarkSubtle:  "rgba(255,255,255,0.65)",  // captions secondaires
  onDarkFaint:   "rgba(255,255,255,0.45)",  // disabled / tertiaire

  // ── Feedback d'appui universel ──
  // Navy à 8% : un seul calque « pressed » qui fonctionne sur carte blanche,
  // fond sable ou surface colorée — remplace les opacités ad hoc par composant.
  pressOverlay: "rgba(11,18,32,0.08)",

  // ── Filigrane orange sur surface sombre ──
  // BrandMark posé sur la carte solde navy : un blanc translucide y serait terne,
  // une pointe d'orange rappelle la marque sans crier.
  orWatermark: "rgba(255,166,102,0.16)",

  // ── Overlays (white tints sur surface colorée) ──
  overlay05: "rgba(255,255,255,0.05)",
  overlay10: "rgba(255,255,255,0.10)",
  overlay15: "rgba(255,255,255,0.15)",
  overlay20: "rgba(255,255,255,0.20)",
  overlay25: "rgba(255,255,255,0.25)",
} as const;

export type ColorToken = keyof typeof colors;

/** Équivalents SOMBRES de `rawColors`. Miroir du bloc `[data-theme="dark"]` de
 *  index.css — les deux doivent bouger ensemble. */
export const darkColors: Record<keyof typeof rawColors, string> = {
  or900: "#fdf0c8",
  or700: "#f7d979",
  or600: "#d9a814",
  or500: "#f0b90b",
  or300: "#7a5f08",
  or100: "#3d2f06",
  or50: "#2a2005",
  navy900: "#f7f9fc",
  navy800: "#eef1f6",
  navy700: "#dde3ed",
  navy600: "#c9d1e0",
  navy400: "#8b96ad",
  navy200: "#7d88a1",
  navy50: "#1a2130",
  sos600: "#ff6b7a",
  sos400: "#ff5566",
  sos200: "#4a1218",
  success600: "#3ddc97",
  success500: "#14b774",
  success100: "#0e3a26",
  warn600: "#f6bc2f",
  warn300: "#b8860b",
  warn50: "#3d2f10",
  gray500: "#99a3b8",
  gray300: "#7c8699",
  gray200: "#2a3345",
  gray100: "#1e2637",
  gray50: "#161d2b",
  emerald700: "#4ade9f",
  emerald600: "#34d399",
  emerald500: "#10b981",
  emerald100: "#0d3b2a",
  emerald50: "#0a2a1e",
  gold500: "#f0b95c",
  gold300: "#b8863f",
  black: "#eef1f6",
  white: "#10151f",
  cream: "#141a26",
  onDark: "#ffffff",
  onDarkStrong: "rgba(255,255,255,0.85)",
  onDarkMuted: "rgba(255,255,255,0.72)",
  onDarkSubtle: "rgba(255,255,255,0.65)",
  onDarkFaint: "rgba(255,255,255,0.45)",
  pressOverlay: "rgba(255,255,255,0.10)",
  orWatermark: "rgba(255,166,102,0.16)",
  overlay05: "rgba(255,255,255,0.05)",
  overlay10: "rgba(255,255,255,0.10)",
  overlay15: "rgba(255,255,255,0.15)",
  overlay20: "rgba(255,255,255,0.20)",
  overlay25: "rgba(255,255,255,0.25)",
};

/**
 * Tokens consommés par l'app — chacun renvoie une variable CSS.
 *
 * `colors.navy800` vaut `"var(--c-navy800)"`, pas `"#0b1220"`. Le navigateur
 * résout la valeur selon le thème actif, y compris dans un style inline.
 */
export const colors = Object.fromEntries(
  Object.keys(rawColors).map((token) => [token, `var(--c-${token})`]),
) as Record<keyof typeof rawColors, string>;

/**
 * Couleur LITTÉRALE du thème actif — pour ce qui n'est pas du CSS.
 *
 * Le SDK Google Maps (`fillColor`, `strokeColor`), un canevas ou toute lib qui
 * parse elle-même la couleur ne comprennent pas `var(--c-x)` : ils reçoivent la
 * chaîne telle quelle et ne dessinent rien. Cette fonction lit la valeur
 * calculée sur `<html>` et rend un vrai `#rrggbb`.
 *
 * Hors navigateur (tests, rendu serveur), on retombe sur la palette claire.
 */
export function themeColor(token: keyof typeof rawColors): string {
  if (typeof document === "undefined") return rawColors[token];
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--c-${token}`)
    .trim();
  return value || rawColors[token];
}
